import { isMatch, merge } from 'lodash-es'
import { useRef } from 'react'
import type { NormalizedCacheObject } from '@apollo/client'
import { initializeApollo, setRequestLinkBusinessId } from 'lib/apollo-client'

interface UseInitializedApolloClientOptions {
  businessId: string | null
  initialCache: NormalizedCacheObject | undefined
  pathName: string
}

export const useInitializedApolloClient = ({
  businessId,
  initialCache,
  pathName,
}: UseInitializedApolloClientOptions) => {
  setRequestLinkBusinessId(businessId)
  const businessRef = useRef(businessId)
  const apolloClientRef = useRef<ReturnType<typeof initializeApollo> | null>(null)

  if (apolloClientRef.current === null) {
    apolloClientRef.current = initializeApollo()
  }
  const currentPathName = useRef<string | null>(null)

  const initializeCache = () => {
    const cache = apolloClientRef.current?.cache

    if (!initialCache || !cache) {
      return
    }

    // `client.cache` is typed as the abstract `ApolloCache`, whose `extract()` returns `unknown`.
    // The client is always built on an `InMemoryCache`, which serializes to this shape.
    const currentCache = cache.extract() as NormalizedCacheObject

    if (isMatch(currentCache, initialCache)) {
      return
    }

    cache.restore(merge({}, currentCache, initialCache))
  }

  if (pathName !== currentPathName.current || businessRef.current !== businessId) {
    if (businessRef.current !== businessId) {
      apolloClientRef.current = initializeApollo({ businessId })
    }
    initializeCache()
    businessRef.current = businessId
    currentPathName.current = pathName
  }

  return apolloClientRef.current
}
